import React, { useState, useEffect } from 'react';
import api from '../services/api';

function AdminManagement() {
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ username: '', password: '' });
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await api.get('/auth/admins');
      setAdmins(response.data);
    } catch (error) {
      setError('Error fetching administrators');
      console.error('Error fetching admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const addAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newAdmin.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      await api.post('/auth/admins', newAdmin);
      setNewAdmin({ username: '', password: '' });
      setSuccess('Administrator added successfully');
      fetchAdmins();
    } catch (error) {
      setError(error.response?.data?.message || 'Error adding administrator');
    }
  };

  const deleteAdmin = async (id) => {
    if (admins.length <= 1) {
      setError('Cannot delete the last administrator');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this administrator?')) {
      return;
    }

    try {
      await api.delete(`/auth/admins/${id}`);
      setSuccess('Administrator deleted successfully');
      fetchAdmins();
    } catch (error) {
      setError(error.response?.data?.message || 'Error deleting administrator');
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      await api.put(`/auth/admins/${editingAdmin.id}/password`, {
        password: newPassword
      });
      setEditingAdmin(null);
      setNewPassword('');
      setConfirmPassword('');
      setSuccess('Password changed successfully');
    } catch (error) {
      setError(error.response?.data?.message || 'Error changing password');
    }
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  if (loading) return <div>Loading administrators...</div>;

  return (
    <div>
      <h2>Administrator Management</h2>
      
      {error && (
        <div style={{
          color: '#dc3545',
          backgroundColor: '#f8d7da',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          {error}
          <button onClick={clearMessages} style={{float: 'right', background: 'none', border: 'none', fontSize: '16px'}}>×</button>
        </div>
      )}

      {success && (
        <div style={{
          color: '#155724',
          backgroundColor: '#d4edda',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '20px',
          border: '1px solid #c3e6cb'
        }}>
          {success}
          <button onClick={clearMessages} style={{float: 'right', background: 'none', border: 'none', fontSize: '16px'}}>×</button>
        </div>
      )}
      
      <form onSubmit={addAdmin} style={{marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '4px'}}>
        <h3>Add New Administrator</h3>
        <div style={{display: 'flex', gap: '10px', marginBottom: '10px'}}>
          <input
            type="text"
            placeholder="Username"
            value={newAdmin.username}
            onChange={(e) => setNewAdmin({...newAdmin, username: e.target.value})}
            style={{flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
            required
          />
          <input
            type="password"
            placeholder="Password (min 6 characters)"
            value={newAdmin.password}
            onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
            style={{flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px'}}
            required
            minLength="6"
          />
        </div>
        <button type="submit" style={{
          padding: '8px 16px',
          backgroundColor: '#28a745',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}>
          Add Administrator
        </button>
      </form>

      <div>
        <h3>Current Administrators ({admins.length})</h3>
        {admins.length === 0 ? (
          <p>No administrators found.</p>
        ) : (
          <table style={{width: '100%', borderCollapse: 'collapse'}}>
            <thead>
              <tr style={{backgroundColor: '#f8f9fa'}}>
                <th style={{padding: '10px', border: '1px solid #ddd', textAlign: 'left'}}>Username</th>
                <th style={{padding: '10px', border: '1px solid #ddd', textAlign: 'left'}}>Created</th>
                <th style={{padding: '10px', border: '1px solid #ddd', textAlign: 'left'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin.id}>
                  <td style={{padding: '10px', border: '1px solid #ddd'}}>{admin.username}</td>
                  <td style={{padding: '10px', border: '1px solid #ddd'}}>
                    {new Date(admin.created_at).toLocaleDateString()}
                  </td>
                  <td style={{padding: '10px', border: '1px solid #ddd'}}>
                    <button
                      onClick={() => setEditingAdmin(admin)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        marginRight: '5px'
                      }}
                    >
                      Change Password
                    </button>
                    {admins.length > 1 && (
                      <button
                        onClick={() => deleteAdmin(admin.id)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Password Change Modal */}
      {editingAdmin && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            width: '400px'
          }}>
            <h3>Change Password for {editingAdmin.username}</h3>
            <form onSubmit={changePassword}>
              <div style={{marginBottom: '15px'}}>
                <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>
                  New Password:
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                  required
                  minLength="6"
                />
              </div>
              <div style={{marginBottom: '20px'}}>
                <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>
                  Confirm Password:
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                  required
                  minLength="6"
                />
              </div>
              <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                <button
                  type="button"
                  onClick={() => {
                    setEditingAdmin(null);
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminManagement;
